import {
  ApiBlueprint,
  createApiRef,
  discoveryApiRef,
  fetchApiRef,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/frontend-plugin-api';

export interface KargoDeploymentMatrix {
  project: {
    name: string;
    webUrl: string;
  };
  environments: Array<{
    name: string;
    upstreamEnvironment?: string;
    health: string;
    issues: string[];
    lastPromotionStatus?: string;
    lastPromotedAt?: string;
  }>;
  artifacts: Array<{
    name: string;
    environments: Record<
      string,
      {
        freightName: string;
        version: string;
        digest: string;
        repository: string;
      }
    >;
  }>;
}

export interface KargoPromotionResult {
  project: string;
  sourceEnvironment: string;
  targetEnvironment: string;
  promotionsRequested: number;
}

export interface KargoDeploymentsApi {
  getDeploymentMatrix(project: string): Promise<KargoDeploymentMatrix>;
  promoteEnvironment(
    project: string,
    environment: string,
  ): Promise<KargoPromotionResult>;
}

export const kargoDeploymentsApiRef = createApiRef<KargoDeploymentsApi>().with({
  id: 'plugin.gitlab-jobs.kargo-deployments',
});

export class KargoDeploymentsClient implements KargoDeploymentsApi {
  readonly #discoveryApi: DiscoveryApi;
  readonly #fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.#discoveryApi = options.discoveryApi;
    this.#fetchApi = options.fetchApi;
  }

  async getDeploymentMatrix(project: string): Promise<KargoDeploymentMatrix> {
    const baseUrl = await this.#discoveryApi.getBaseUrl('gitlab-jobs');
    const response = await this.#fetchApi.fetch(
      `${baseUrl}/kargo/projects/${encodeURIComponent(project)}/deployments`,
    );
    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('You are not permitted to view Kargo deployments.');
      }
      if (response.status === 404) {
        throw new Error('The Kargo project is unavailable.');
      }
      throw new Error('Unable to load Kargo deployments.');
    }
    return response.json() as Promise<KargoDeploymentMatrix>;
  }

  async promoteEnvironment(
    project: string,
    environment: string,
  ): Promise<KargoPromotionResult> {
    const baseUrl = await this.#discoveryApi.getBaseUrl('gitlab-jobs');
    const response = await this.#fetchApi.fetch(
      `${baseUrl}/kargo/projects/${encodeURIComponent(
        project,
      )}/environments/${encodeURIComponent(environment)}/promotions`,
      { method: 'POST' },
    );
    if (!response.ok) {
      if (response.status === 403) {
        throw new Error(`You are not permitted to promote to ${environment}.`);
      }
      throw new Error(`Unable to promote to ${environment}.`);
    }
    return response.json();
  }
}

export const kargoDeploymentsApi = ApiBlueprint.make({
  name: 'kargo-deployments',
  params: defineParams =>
    defineParams({
      api: kargoDeploymentsApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
      },
      factory: ({ discoveryApi, fetchApi }) =>
        new KargoDeploymentsClient({ discoveryApi, fetchApi }),
    }),
});
